/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import CategoryDropdown from '@/components/CategoryDropDown';
import DialogBox2 from '@/components/DialogBox2';
import SimpleKeyboardTextarea from '@/components/SimpleKeyboardTextarea';
import { useSnackbar } from '@/hooks/useSnackbar';
import useDialog from '@/hooks/util/useDialog';
import useStudentProfile from '@/pages/StudentProfile/hooks/useStudentProfile';
import useStore from '@/store';

export default function SubmitFeedbackDialog(): ReactElement {
  const { isOpen, closeDialog, updateDialogProperties } = useDialog({ open: false });
  const { currentStudent } = useStore();
  const { showSnackbar } = useSnackbar();
  const [input, setInput] = useState('');
  const { t } = useTranslation(['studentProfile', 'adminLogin']);
  const { onSubmitFeedback } = useStudentProfile();
  const [selectedCategory, onSetSelectedCategory] = useState('');
  useEffect(() => {
    const cleanup = window.electronAPI?.onGiveFeedback(() => {
      if (currentStudent) {
        updateDialogProperties({ open: true });
      } else {
        showSnackbar('Please login first as a student.');
      }
    });
    return () => {
      cleanup();
    };
  }, [updateDialogProperties, currentStudent, showSnackbar]);
  const onClose = () => {
    closeDialog();
  };

  const onInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value);
  }, []);

  const onSubmit = () => {
    if (input.trim().length < 2) {
      showSnackbar(t('adminLogin:giveFeedback'));
    } else if (selectedCategory.trim().length === 0) {
      showSnackbar(t('adminLogin:selectCategory'));
    } else {
      onSubmitFeedback(input, selectedCategory, closeDialog);
      setInput('');
      onSetSelectedCategory('');
    }
  };
  const isSubmitDisabled = useMemo(
    () => input.trim().length < 2 || selectedCategory.trim().length === 0,
    [input, selectedCategory],
  );

  const getFeedbackForm = () => {
    return (
      <div className="w-full min-w-[600px] rounded-2xl rounded-l-2xl bg-white px-8 pb-8 pt-8">
        <div className="mt-4 flex w-full items-center">
          <p className="mb-4 mr-4 font-lilita text-[1.75em] text-secondary-blue-3">Feedback form</p>
        </div>
        <div className="mt-2 flex flex-col items-end justify-end gap-4 ">
          <CategoryDropdown
            selectedCategory={selectedCategory}
            onCategorySelect={onSetSelectedCategory}
          />
          <SimpleKeyboardTextarea
            name="Submit Feedback"
            className="w-full resize-none rounded-2xl border-2 border-neutral-grey-3 p-3 pr-10 placeholder-extended-color-3 placeholder:text-lg placeholder:font-light"
            placeholder={t('studentProfile:feedbackPlaceholder')}
            rows={3}
            maxLength={512}
            value={input}
            onChange={onInputChange}
          />
          <button
            type="submit"
            onClick={onSubmit}
            className={`text-md z-0 w-[185px] rounded-[72px] ${
              isSubmitDisabled
                ? 'cursor-not-allowed bg-primary-yellow-1 text-primary-blue-1'
                : 'bg-primary-yellow-3 text-primary-blue-3'
            }  px-4 py-[13px] font-semibold shadow-custom`}
          >
            {t('StudentProfile:Submit')}
          </button>
        </div>
      </div>
    );
  };
  return isOpen ? (
    <div className="absolute top-0 z-50 h-screen w-screen">
      <DialogBox2
        open={isOpen}
        onClose={onClose}
        styles="!px-0 !max-w-[838px] !py-0"
        showCloseIcon
        dismissable
      >
        {getFeedbackForm()}
      </DialogBox2>
    </div>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );
}
